import { Tag } from 'src/components/atoms';
import {
  StyledCardContainer as BaseStyledCardContainer,
  TextGroup as BaseTextGroup,
} from 'src/components/organisms/Common/CategoryCard/CategoryCard.styles';
import { textStyleCss } from 'src/theme/atlas';
import styled, { css } from 'styled-components';

const categoryCardLinkFocusRing = css`
  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.turquoise.T300
          : props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
  }
`;

export { StyledCardContainer } from 'src/components/organisms/Common/CategoryCard';

export const CategoryName = styled.h3`
  margin: 0;
  width: 100%;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.headingH3)}
  font-weight: 600;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CategoryCardTextGroup = styled(BaseTextGroup)`
  width: 100%;
  min-width: 0;
  gap: 16px;
`;

export const CategoryCardBodyContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: visible;
`;

export const CategoryCardTopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

export const CategoryDescriptionSlot = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 60px;
  max-height: 60px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N0
        : props.theme.atlas.semantic.color.surface.default};
  }
`;

export const CategoryRichTextClamped = styled.div`
  width: 100%;
  max-height: 60px;
  overflow: hidden;
  box-sizing: border-box;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.muted};
  line-height: 20px;
  letter-spacing: 0.2px;

  .ql-container {
    width: 100%;
  }

  .ql-editor {
    padding: 0;
    width: 100%;
  }

  p {
    margin: 0;
  }
`;

export const DescriptionSuffixStack = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 2px;
  line-height: 20px;
  padding-left: 2rem;
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N0
          : props.theme.atlas.semantic.color.surface.default}
      45%
  );
`;

export const DescriptionInlineToggle = styled.button`
  position: relative;
  top: -5px;
  margin: 0;
  padding: 0;
  padding-bottom: 2px;
  border: none;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N400
        : props.theme.atlas.core.color.gray[400]};
  cursor: pointer;
  flex: 0 0 auto;
  background: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.surface.default};
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmallSemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.turquoise.T300
      : props.theme.atlas.semantic.color.type.muted};
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.turquoise.T400
        : props.theme.atlas.semantic.color.action.primary.hover};
  }

  ${categoryCardLinkFocusRing}
`;

export const InteractiveCardContainer = styled(BaseStyledCardContainer)<{
  $clickable: boolean;
  $descriptionExpanded?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  box-shadow: none;
  min-height: 220px;
  height: ${(p) => (p.$descriptionExpanded ? 'auto' : '220px')};

  > div {
    padding: 24px;
  }

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid
          ${(x) =>
            x.theme.isVaultVariant
              ? x.theme.newPalette.turquoise.T300
              : x.theme.atlas.semantic.color.ui.focusRing};
        outline-offset: 2px;
      }
    `}
`;

export const CardTagsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  justify-content: flex-start;
`;

export const CountryPillTag = styled(Tag)`
  box-sizing: border-box;
  height: 24px;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 9999px !important;
  border: 1px solid
    ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N500
        : props.theme.atlas.core.color.gray[500]} !important;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default} !important;
  background-color: transparent !important;

  &:hover {
    background-color: transparent !important;
  }
`;

export const CountryPillButton = styled.button`
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: inline-flex;
  border-radius: 3px;

  &,
  * {
    cursor: pointer;
  }

  ${categoryCardLinkFocusRing}
`;

export const ShowMoreLink = styled.span`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmallSemibold
      : textStyleCss(theme.atlas.typography.semantic.caption)}
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.turquoise.T300
      : props.theme.atlas.semantic.color.action.primary.default};
  text-decoration: underline;
  text-decoration-skip-ink: none;
  cursor: pointer;

  &:hover {
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.turquoise.T400
        : props.theme.atlas.semantic.color.action.primary.default};
  }

  ${categoryCardLinkFocusRing}
`;
